/**
 * TicketChatBubble — renders a single chat message.
 * RN messages align right (teal). Others align left (white card).
 * Shows sender name, timestamp, and delivery status for RN messages.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import * as Linking from "expo-linking";
import { ActivityIndicator, Image, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { LinkifiedText } from "../LinkifiedText";

import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";
import type { Attachment } from "@/lib/ticket-api";

export interface ChatBubbleMessage {
    id: string;
    content: string;
    senderType: string;
    senderName: string | null;
    createdAt: string;
    deliveryStatus: "sending" | "delivered" | "failed";
    attachments?: Attachment[];
}

function formatTime(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function senderLabel(senderType: string, senderName: string | null): string | null {
    if (senderType === "rn") {
        return null;
    }
    if (senderName) {
        return senderName;
    }
    const labels: Record<string, string> = {
        bot: "TaskHealth Bot",
        admin: "TaskHealth",
        system: "System"
    };
    return labels[senderType] ?? senderType;
}

function formatSize(bytes: number): string {
    if (bytes <= 0) {
        return "";
    }
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Attachment strip inside a bubble. Images render as tappable thumbnails, other
 * files as a labelled chip. Both open the presigned `viewUrl` in the system
 * viewer — the bucket is private, so the raw `url` would 403.
 */
function AttachmentBlock({ attachments, isRn }: { attachments: Attachment[]; isRn: boolean }) {
    const open = (a: Attachment) => {
        const target = a.viewUrl ?? a.url;
        if (target) {
            void Linking.openURL(target);
        }
    };

    return (
        <View style={styles.attachmentWrap}>
            {attachments.map((a, i) => {
                const isImage = a.type === "image" || a.mimeType?.startsWith("image/");
                return isImage && a.viewUrl ? (
                    <TouchableOpacity
                        key={`${a.url}-${i}`}
                        onPress={() => open(a)}
                        activeOpacity={0.85}
                        accessibilityLabel={`Open image ${a.filename}`}
                    >
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: a.viewUrl }}
                            style={styles.attachmentImage}
                            // `contain`, not the default `cover`: these are
                            // photographed documents, and a square centre-crop
                            // cut the edges off a landscape license (the whole
                            // point is that the reader can read it).
                            resizeMode="contain"
                        />
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity
                        key={`${a.url}-${i}`}
                        style={[styles.fileChip, isRn ? styles.fileChipRn : styles.fileChipOther]}
                        onPress={() => open(a)}
                        activeOpacity={0.7}
                        accessibilityLabel={`Open file ${a.filename}`}
                    >
                        <MaterialCommunityIcons
                            name="file-pdf-box"
                            size={22}
                            color={isRn ? colors.surface : colors.error}
                        />
                        <View style={styles.fileChipText}>
                            <Text
                                style={[styles.fileName, isRn ? styles.rnText : styles.otherText]}
                                numberOfLines={1}
                            >
                                {a.filename}
                            </Text>
                            {formatSize(a.size) !== "" && (
                                <Text
                                    style={[
                                        styles.fileSize,
                                        isRn ? styles.rnFileSize : styles.otherFileSize
                                    ]}
                                >
                                    {formatSize(a.size)}
                                </Text>
                            )}
                        </View>
                    </TouchableOpacity>
                );
            })}
        </View>
    );
}

export function TicketChatBubble({ message }: { message: ChatBubbleMessage }) {
    const isRn = message.senderType === "rn";
    const label = senderLabel(message.senderType, message.senderName);
    const attachments = message.attachments ?? [];

    return (
        <View style={[styles.bubbleRow, isRn ? styles.bubbleRowRight : styles.bubbleRowLeft]}>
            <View style={[styles.bubble, isRn ? styles.rnBubble : styles.otherBubble]}>
                {label && <Text style={styles.senderName}>{label}</Text>}
                {attachments.length > 0 && (
                    <AttachmentBlock attachments={attachments} isRn={isRn} />
                )}
                {message.content !== "" && (
                    <LinkifiedText
                        style={[styles.bubbleText, isRn ? styles.rnText : styles.otherText]}
                        linkColor={isRn ? colors.surface : colors.teal600}
                    >
                        {message.content}
                    </LinkifiedText>
                )}
            </View>
            <View style={[styles.metaRow, isRn && styles.metaRowRight]}>
                <Text style={styles.timeText}>{formatTime(message.createdAt)}</Text>
                {isRn && message.deliveryStatus === "sending" && (
                    <ActivityIndicator
                        size={10}
                        color={colors.textTertiary}
                        style={{ marginLeft: 4 }}
                    />
                )}
                {isRn && message.deliveryStatus === "delivered" && (
                    <MaterialCommunityIcons
                        name="check"
                        size={12}
                        color={colors.textTertiary}
                        style={{ marginLeft: 4 }}
                    />
                )}
                {isRn && message.deliveryStatus === "failed" && (
                    <MaterialCommunityIcons
                        name="alert-circle"
                        size={12}
                        color={colors.error}
                        style={{ marginLeft: 4 }}
                    />
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    bubbleRow: { marginBottom: spacing.sm },
    bubbleRowRight: { alignItems: "flex-end" },
    bubbleRowLeft: { alignItems: "flex-start" },
    bubble: { maxWidth: "80%", padding: spacing.md, borderRadius: radius.lg },
    rnBubble: { backgroundColor: colors.teal500, borderBottomRightRadius: radius.xs },
    otherBubble: {
        backgroundColor: colors.surface,
        borderBottomLeftRadius: radius.xs,
        ...shadows.xs
    },
    senderName: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.teal600,
        marginBottom: 2
    },
    bubbleText: { fontFamily: fonts.regular, fontSize: 15, lineHeight: 21 },
    rnText: { color: colors.surface },
    otherText: { color: colors.textPrimary },
    metaRow: { flexDirection: "row", alignItems: "center", marginTop: 2, paddingHorizontal: 4 },
    metaRowRight: { justifyContent: "flex-end" },
    timeText: { fontFamily: fonts.regular, fontSize: 11, color: colors.textTertiary },
    attachmentWrap: { gap: spacing.xs, marginBottom: spacing.xs },
    attachmentImage: {
        // Landscape-ish box: photographed documents and forms are far more
        // often wide than square, and `contain` letterboxes the rest.
        width: 220,
        height: 165,
        borderRadius: radius.md,
        backgroundColor: colors.gray100
    },
    fileChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        borderRadius: radius.md,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.sm,
        minWidth: 180
    },
    fileChipRn: { backgroundColor: "rgba(255, 255, 255, 0.18)" },
    fileChipOther: { backgroundColor: colors.gray100 },
    fileChipText: { flex: 1 },
    fileName: { fontFamily: fonts.medium, fontSize: 13 },
    fileSize: { fontFamily: fonts.regular, fontSize: 11 },
    rnFileSize: { color: "rgba(255, 255, 255, 0.75)" },
    otherFileSize: { color: colors.textTertiary }
});
